/**
 * The contact inspector — the `inspector` dock kind's first renderer
 * (2026-08-28). Opens when a card on /contacts is clicked. DIRECT editing
 * (owner: "inspector is for edit, show fields directly") — no view/edit
 * mode split: the fields render editable immediately, Save persists,
 * Delete removes the card. A quick-actions row keeps the card's whole
 * point — reaching the person — one click away (mailto/tel/website/graph
 * from the SAVED values, not the in-progress form). Self-contained: it
 * fetches the full card itself (the page's list payload trims bodies, and
 * a dock panel outlives the page's state), and announces every mutation
 * on the `agntspace:contacts-changed` bus so the page reloads its list.
 */
import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Globe, Mail, Network, Phone } from "lucide-react";
import { Button } from "../../../common/Button";
import { Modal } from "../../../common/Modal";
import { toast } from "../../../../stores/toast";
import { useDockStore, type DockPanel } from "../../../../stores/dock";
import { ContactFormFields } from "../../../contacts/ContactFormFields";
import {
	apiError,
	contactValue,
	type ContactForm,
	EMPTY_CONTACT_FORM,
	emitContactsChanged,
	fetchCategoryOptions,
	normalizeWebsite,
} from "../../../../lib/contactsApi";
import { inspectorData } from "./data";
import styles from "./ContactInspectorBody.module.css";

/** The stored select values are internal. "agent" on a person reads as
 *  the person, so the opened card says who filed it. */
const CONTACT_SOURCE_LABELS: Record<string, string> = {
	manual: "Added by you",
	agent: "From the agent",
	google: "Google",
};

function contactSourceLabel(source: string): string {
	const raw = source.trim();
	if (!raw) return "";
	return CONTACT_SOURCE_LABELS[raw.toLowerCase()] ?? raw;
}

interface FullCard {
	slug: string;
	title: string;
	values: Record<string, unknown>;
	body: string;
}

function formFromCard(card: FullCard): ContactForm {
	const v = (key: string) => contactValue(card, key);
	return {
		name: card.title,
		email: v("email"),
		phone: v("phone"),
		company: v("company"),
		role: v("role"),
		birthday: v("birthday"),
		category: v("category"),
		nickname: v("nickname"),
		pronouns: v("pronouns"),
		relationship: v("relationship"),
		address: v("address"),
		website: v("website"),
		notes: card.body,
	};
}

export function ContactInspectorBody({ panel }: { panel: DockPanel }) {
	const slug = String(inspectorData(panel).props.slug ?? "");
	const [card, setCard] = useState<FullCard | null>(null);
	const [missing, setMissing] = useState(false);
	const [form, setForm] = useState<ContactForm>(EMPTY_CONTACT_FORM);
	const [saving, setSaving] = useState(false);
	const [deleting, setDeleting] = useState(false);
	const [categoryOptions, setCategoryOptions] = useState<string[]>([]);
	const closePanel = useDockStore((s) => s.closePanel);
	const replacePanel = useDockStore((s) => s.replacePanel);

	const load = useCallback(async () => {
		setMissing(false);
		try {
			const res = await fetch(`/api/collections/contacts/items/${slug}`);
			if (res.status === 404) {
				setMissing(true);
				setCard(null);
				return;
			}
			if (!res.ok) throw new Error(`Couldn't load the card (${res.status})`);
			const full = await res.json();
			const loaded: FullCard = {
				slug,
				title: String(full.title ?? ""),
				values: full.values ?? {},
				body: String(full.body ?? ""),
			};
			setCard(loaded);
			// Fields render editable immediately — hydrate on every (re)load.
			// Loads happen on mount and right after a save, so this never
			// clobbers typing mid-edit.
			setForm(formFromCard(loaded));
		} catch (e) {
			toast.error(e instanceof Error ? e.message : "Couldn't load the card");
		}
	}, [slug]);

	useEffect(() => {
		void load();
		void fetchCategoryOptions().then(setCategoryOptions);
	}, [load]);

	if (missing) {
		return (
			<div className={styles.empty}>
				<p>This contact card no longer exists — it was deleted.</p>
				<Button size="sm" variant="secondary" onClick={() => closePanel(panel.id)}>
					Close panel
				</Button>
			</div>
		);
	}
	if (!card) {
		return <div className={styles.empty}>Loading contact…</div>;
	}

	const v = (key: string) => contactValue(card, key);

	const save = async () => {
		if (!form.name.trim()) {
			toast.error("A contact needs a name");
			return;
		}
		setSaving(true);
		try {
			const res = await fetch(`/api/collections/contacts/items/${card.slug}`, {
				method: "PATCH",
				headers: { "Content-Type": "application/json" },
				body: JSON.stringify({
					title: form.name,
					values: {
						email: form.email, phone: form.phone, company: form.company,
						role: form.role, birthday: form.birthday, category: form.category,
						nickname: form.nickname, pronouns: form.pronouns,
						relationship: form.relationship, address: form.address,
						website: normalizeWebsite(form.website),
					},
					body: form.notes,
				}),
			});
			if (!res.ok) {
				const detail = await res.json().catch(() => ({}));
				throw new Error(apiError(detail, `Save failed (${res.status})`));
			}
			toast.success(`Saved “${form.name}”`);
			// Keep the tab label honest after a rename (id is slug-stable).
			if (form.name.trim() !== panel.title) {
				replacePanel(panel.id, { ...panel, title: form.name.trim() });
			}
			emitContactsChanged();
			await load();
		} catch (e) {
			toast.error(e instanceof Error ? e.message : "Save failed");
		} finally {
			setSaving(false);
		}
	};

	// Removing the card is how a contact "moves back to Other people": the
	// entity-linked person stays in the knowledge graph, and the /contacts
	// Other-people section lists exactly the uncarded active persons — so
	// they reappear there on the next reload. The verb says THAT, not
	// "delete", because that is what the user experiences; the card's own
	// details (email/phone/notes) are genuinely discarded and the confirm
	// says so. A card with no entity link has nowhere to "move" — for that
	// rare shape the verb honestly stays "Delete".
	const isMove = Boolean(v("entity"));
	const doDelete = async () => {
		try {
			const res = await fetch(`/api/collections/contacts/items/${card.slug}`, {
				method: "DELETE",
			});
			if (!res.ok) throw new Error(`Remove failed (${res.status})`);
			toast.success(
				isMove
					? `Moved “${card.title}” to Other people`
					: `Deleted “${card.title}”`,
			);
			setDeleting(false);
			emitContactsChanged();
			closePanel(panel.id);
		} catch (e) {
			toast.error(e instanceof Error ? e.message : "Remove failed");
		}
	};

	const website = v("website");
	const websiteHref = website && !/^https?:\/\//i.test(website) ? `https://${website}` : website;
	const sourceLabel = contactSourceLabel(v("source"));
	const hasReach = Boolean(v("email") || v("phone") || website || v("entity"));

	return (
		<div className={styles.body}>
			{/* Reach the person / their graph node in one click, from the
			    SAVED card — these are actions, the fields below are the data. */}
			{hasReach && (
				<div className={styles.quickRow}>
					{v("email") && (
						<a href={`mailto:${v("email")}`} className={styles.quickLink} title={`Email ${v("email")}`}>
							<Mail size={13} aria-hidden /> Email
						</a>
					)}
					{v("phone") && (
						<a href={`tel:${v("phone")}`} className={styles.quickLink} title={`Call ${v("phone")}`}>
							<Phone size={13} aria-hidden /> Call
						</a>
					)}
					{website && (
						<a href={websiteHref} target="_blank" rel="noopener noreferrer" className={styles.quickLink} title={website}>
							<Globe size={13} aria-hidden /> Website
						</a>
					)}
					{v("entity") && (
						<Link to={`/graph?entity=${encodeURIComponent(card.title)}`} className={styles.quickLink} title="Open in the knowledge graph">
							<Network size={13} aria-hidden /> Graph
						</Link>
					)}
				</div>
			)}

			<ContactFormFields
				form={form}
				setForm={setForm}
				categoryOptions={categoryOptions}
				onCategoryCreated={(name) =>
					setCategoryOptions((cur) => (cur.includes(name) ? cur : [...cur, name]))
				}
				compact
			/>

			{sourceLabel && <p className={styles.sourceNote}>{sourceLabel}</p>}

			<div className={styles.actions}>
				<Button variant="primary" size="sm" loading={saving} onClick={() => void save()}>
					Save
				</Button>
				<Button variant="danger" size="sm" onClick={() => setDeleting(true)}>
					{isMove ? "Move to Other people…" : "Delete…"}
				</Button>
			</div>

			<Modal
				open={deleting}
				title={isMove ? "Move to Other people" : "Delete contact"}
				dismissOnBackdrop={false}
				onClose={() => setDeleting(false)}
				footer={
					<>
						<Button variant="ghost" onClick={() => setDeleting(false)}>Cancel</Button>
						<Button variant="danger" onClick={() => void doDelete()}>
							{isMove ? "Move" : "Delete"}
						</Button>
					</>
				}
			>
				{isMove ? (
					<p>
						Move <strong>{card.title}</strong> out of your contacts? They stay
						in your knowledge graph and reappear under “Other people” — but
						this card's details (email, phone, notes) are discarded, not
						stored anywhere else.
					</p>
				) : (
					<p>
						Delete the contact card for <strong>{card.title}</strong>? The
						card (email, phone, notes) is removed.
					</p>
				)}
			</Modal>
		</div>
	);
}
